const _jsxFileName = '';
function _optionalChain(ops) {
  let lastAccessLHS = undefined;
  let value = ops[0];
  let i = 1;
  while (i < ops.length) {
    const op = ops[i];
    const fn = ops[i + 1];
    i += 2;
    if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) {
      return undefined;
    }
    if (op === 'access' || op === 'optionalAccess') {
      lastAccessLHS = value;
      value = fn(value);
    } else if (op === 'call' || op === 'optionalCall') {
      value = fn((...args) => value.call(lastAccessLHS, ...args));
      lastAccessLHS = undefined;
    }
  }
  return value;
}
import React, { useEffect, useState } from 'react';
import {
  Filter,
  ChevronDown,
  Heart,
  ShoppingBag,
  Eye,
  X,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Clock,
  Tag,
  LogIn,
  UserPlus,
} from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL;

const PLACEHOLDER_IMAGE = 'https://placehold.co/400x500/f5f5f5/999999?text=No+Image';

const Shop = ({
  initialCategory,
  onNavigate,
  products: initialProducts,
  wishlist,
  onToggleWishlist,
  onAddToCart,
  onSelectProduct,
  user,
}) => {
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [sortBy, setSortBy] = useState('featured');
  const [showFilters, setShowFilters] = useState(false);
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [visibleProducts, setVisibleProducts] = useState([]);

  const [apiProducts, setApiProducts] = useState([]);
  const [apiLoading, setApiLoading] = useState(true);
  const [apiError, setApiError] = useState(null);

  // Dynamically build category list from loaded products
  const categories = React.useMemo(() => {
    const cats = [...new Set(apiProducts.map((p) => p.category).filter(Boolean))].sort();
    return ['all', ...cats];
  }, [apiProducts]);

  const [imageErrors, setImageErrors] = useState({});
  const [wishlistLoading, setWishlistLoading] = useState({});
  const [cartLoading, setCartLoading] = useState({});
  const [pendingWishlistAction, setPendingWishlistAction] = useState({});

  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [promptAction, setPromptAction] = useState('cart');

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    fetchProductsFromAPI();
  }, []);

  useEffect(() => {
    setActiveCategory(initialCategory);
  }, [initialCategory]);

  useEffect(() => {
    const selectedProductJson = localStorage.getItem('selectedProduct');
    const selectedProductId = localStorage.getItem('selectedProductId');

    if (selectedProductJson && onSelectProduct) {
      try {
        const product = JSON.parse(selectedProductJson);
        onSelectProduct(product);
        localStorage.removeItem('selectedProduct');
        localStorage.removeItem('selectedProductId');
      } catch (e) {}
    } else if (selectedProductId && onSelectProduct && apiProducts.length > 0) {
      const product = apiProducts.find((p) => p.id === parseInt(selectedProductId, 10));
      if (product) {
        onSelectProduct(product);
        localStorage.removeItem('selectedProductId');
      }
    }
  }, [apiProducts, onSelectProduct]);

  useEffect(() => {
    setLoading(true);

    const timer = setTimeout(() => {
      const sourceProducts = apiProducts.length > 0 ? apiProducts : initialProducts;

      let filtered = sourceProducts.filter((p) => {
        if (activeCategory === 'all') return true;
        return p.category === activeCategory;
      });

      if (sortBy === 'price-asc') {
        filtered = [...filtered].sort((a, b) => a.price - b.price);
      } else if (sortBy === 'price-desc') {
        filtered = [...filtered].sort((a, b) => b.price - a.price);
      } else if (sortBy === 'newest') {
        filtered = [...filtered].sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
      }

      setVisibleProducts(filtered);
      setLoading(false);
    }, 400);

    return () => clearTimeout(timer);
  }, [activeCategory, sortBy, apiProducts, initialProducts]);

  const heroImages = {
    all: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?q=80&w=2070&auto=format&fit=crop',
    jewelry:
      'https://images.unsplash.com/photo-1573408301185-9146fe634ad0?q=80&w=2000&auto=format&fit=crop',
    bags: 'https://images.unsplash.com/photo-1548036328-c9fa89d128fa?q=80&w=2069&auto=format&fit=crop',
    fragrances:
      'https://images.unsplash.com/photo-1616949755610-8c9bbc08f138?q=80&w=2070&auto=format&fit=crop',
  };

  const heroTitles = {
    all: 'The Grand Collection',
    jewelry: 'High Jewelry',
    bags: 'Leather Goods',
    fragrances: 'Haute Parfumerie',
  };

  const heroSubtitles = {
    all: 'Explore our complete range of hand-selected luxuries.',
    jewelry: 'Diamonds, gemstones, and precious metals crafted for eternity.',
    bags: 'Exquisite Italian craftsmanship meeting modern utility.',
    fragrances: 'Scented masterpieces to define your presence.',
  };

  function showToast(message, type = 'success') {
    const toast = document.createElement('div');
    toast.className = [
      'fixed top-24 right-4 text-white px-6 py-3 rounded-sm shadow-lg z-[300]',
      type === 'success' ? 'bg-green-600' : 'bg-red-600',
    ].join(' ');
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  }

  function getUserId() {
    if (_optionalChain([user, 'optionalAccess', (_2) => _2.id])) return user.id;

    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const parsedUser = JSON.parse(userStr);
        if (_optionalChain([parsedUser, 'optionalAccess', (_3) => _3.id]))
          return Number(parsedUser.id);
      }

      const id = localStorage.getItem('userId');
      return id ? parseInt(id, 10) : null;
    } catch (e2) {
      return null;
    }
  }

  function getGuestId() {
    let guestId = localStorage.getItem('guest_id');
    if (!guestId) {
      guestId = 'guest_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('guest_id', guestId);
    }
    return guestId;
  }

  function isAuthenticated() {
    return !!getUserId();
  }

  async function fetchProductsFromAPI() {
    setApiLoading(true);
    setApiError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/products`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const productsArray = data.products || data.data || data;

      if (!Array.isArray(productsArray)) {
        setApiProducts(initialProducts);
        return;
      }

      const transformedProducts = productsArray.map((item) => {
        let galleryImages = [];

        if (item.gallery_images) {
          try {
            if (typeof item.gallery_images === 'string') {
              try {
                const parsed = JSON.parse(item.gallery_images);
                galleryImages = Array.isArray(parsed) ? parsed : [];
              } catch (e3) {
                if (item.gallery_images.includes(',')) {
                  galleryImages = item.gallery_images.split(',').map((url) => url.trim());
                } else if (item.gallery_images.length > 10) {
                  galleryImages = [item.gallery_images];
                }
              }
            } else if (Array.isArray(item.gallery_images)) {
              galleryImages = item.gallery_images;
            }

            galleryImages = galleryImages.filter(
              (img) => img && typeof img === 'string' && img.length > 10
            );
          } catch (e4) {
            galleryImages = [];
          }
        }

        let weight = undefined;
        if (item.weight) {
          const parsedWeight = parseFloat(item.weight);
          weight = !isNaN(parsedWeight) ? parsedWeight : item.weight;
        }

        // FIXED: Ensure image is properly mapped from database
        let productImage = PLACEHOLDER_IMAGE;
        if (item.image_url && item.image_url.trim() !== '') {
          productImage = item.image_url;
        } else if (item.image && item.image.trim() !== '') {
          productImage = item.image;
        }

        return {
          id: Number(item.id),
          name: item.name,
          description: item.description || '',
          price: parseFloat(item.price) || 0,
          image: productImage,
          galleryImages,
          category: item.category || 'jewelry',
          isNew: item.is_new == 1 || false,
          isBestSeller: item.is_best_seller == 1 || false,
          condition: item.condition || 'Brand New',
          weight,
          karat: item.karat || '',
          goldType: item.gold_type || item.goldType || '',
          stock:
            item.stock === null || item.stock === undefined ? 0 : parseInt(item.stock, 10) || 0,
          isPreOrder: item.isPreOrder == 1 || false,
          preOrderReleaseDate: item.preOrderReleaseDate || null,
          priceOverridden: item.priceOverridden == 1 || false,
          is_promo: item.is_promo == 1 || false,
          promo_price: item.promo_price ? parseFloat(item.promo_price) : null,
          promo_percentage: item.promo_percentage ? parseInt(item.promo_percentage, 10) : null,
          promo_label: item.promo_label || null,
          promo_start_date: item.promo_start_date || null,
          promo_end_date: item.promo_end_date || null,
        };
      });

      setApiProducts(transformedProducts);
    } catch (error) {
      setApiError('Failed to load products from server');
      setApiProducts(initialProducts);
    } finally {
      setApiLoading(false);
    }
  }

  const handleImageError = (productId) => {
    if (!imageErrors[productId]) {
      setImageErrors((prev) => ({ ...prev, [productId]: true }));
    }
  };

  const getProductImage = (product) => {
    if (imageErrors[product.id]) {
      return PLACEHOLDER_IMAGE;
    }
    if (product.image && product.image.trim() !== '' && product.image.length > 5) {
      return product.image;
    }
    return PLACEHOLDER_IMAGE;
  };

  const openLightbox = (images, index, e) => {
    e.stopPropagation();
    if (images && images.length > 0) {
      setLightboxImages(images);
      setCurrentImageIndex(index);
      setLightboxOpen(true);
    }
  };

  const isInWishlist = (id) => {
    return wishlist.some((item) => item.id === id);
  };

  const handleProductClick = (product) => {
    if (onSelectProduct) {
      onSelectProduct(product);
    }
  };

  // FIXED: Log the product being added to cart
  const handleAddToCart = async (e, product) => {
    e.stopPropagation();

    if (!user && !isAuthenticated()) {
      const guestId = getGuestId();
      if (!guestId) {
        setPromptAction('cart');
        setShowLoginPrompt(true);
        return;
      }
    }

    if (typeof product.stock === 'number' && product.stock <= 0) {
      showToast('This item is out of stock.', 'error');
      return;
    }

    setCartLoading((prev) => ({ ...prev, [product.id]: true }));

    try {
      // The onAddToCart will call the API with the product
      // The backend will fetch the product image from the database
      const result = await Promise.resolve(onAddToCart(product, 1));

      if (result === false) {
        showToast('Failed to add item to cart. Please try again.', 'error');
      } else {
        showToast('Added to cart successfully');
      }
    } catch (error) {
      showToast('Failed to add item to cart. Please try again.', 'error');
    } finally {
      setCartLoading((prev) => ({ ...prev, [product.id]: false }));
    }
  };

  const handleWishlistToggle = async (e, product) => {
    e.stopPropagation();
    e.preventDefault();

    if (pendingWishlistAction[product.id] || wishlistLoading[product.id]) {
      return;
    }

    if (!user && !isAuthenticated()) {
      const guestId = getGuestId();
      if (!guestId) {
        setPromptAction('wishlist');
        setShowLoginPrompt(true);
        return;
      }
    }

    setPendingWishlistAction((prev) => ({ ...prev, [product.id]: true }));
    setWishlistLoading((prev) => ({ ...prev, [product.id]: true }));

    try {
      const currentlyInWishlist = isInWishlist(product.id);

      await Promise.resolve(onToggleWishlist(product));

      showToast(currentlyInWishlist ? 'Removed from wishlist' : 'Added to wishlist', 'success');
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to update wishlist. Please try again.',
        'error'
      );
    } finally {
      setWishlistLoading((prev) => ({ ...prev, [product.id]: false }));
      setPendingWishlistAction((prev) => ({ ...prev, [product.id]: false }));
    }
  };

  const refreshProducts = () => {
    setImageErrors({});
    fetchProductsFromAPI();
  };

  const formatPrice = (price) => {
    return `₱${price.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getConditionBadgeStyle = (condition) => {
    switch (condition) {
      case 'Brand New':
        return 'bg-emerald-500/90 text-white';
      case 'Pre-loved':
      case 'Pre-loved (Used)':
        return 'bg-amber-500/90 text-white';
      case 'Vintage':
        return 'bg-purple-500/90 text-white';
      case 'Like New':
        return 'bg-blue-500/90 text-white';
      case 'Good':
        return 'bg-green-500/90 text-white';
      case 'Fair':
        return 'bg-orange-500/90 text-white';
      default:
        return 'bg-stone-800/90 text-gold-200';
    }
  };

  const getConditionDisplay = (condition) => {
    switch (condition) {
      case 'Pre-loved':
      case 'Pre-loved (Used)':
        return '🔄 Pre-loved';
      case 'Vintage':
        return '📅 Vintage';
      case 'Like New':
        return '✨ Like New';
      case 'Good':
        return '👍 Good';
      case 'Fair':
        return '⚠️ Fair';
      default:
        return condition;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (e5) {
      return null;
    }
  };

  return React.createElement(
    'div',
    {
      className: 'bg-white min-h-screen pt-20',
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 496 },
    },
    showLoginPrompt &&
      React.createElement(
        'div',
        {
          className: 'fixed inset-0 z-[300] flex items-center justify-center',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 498 },
        },
        React.createElement('div', {
          className: 'absolute inset-0 bg-stone-900/80 backdrop-blur-md',
          onClick: () => setShowLoginPrompt(false),
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 499 },
        }),
        React.createElement(
          'div',
          {
            className:
              'relative bg-white w-full max-w-md rounded-sm shadow-2xl mx-4 overflow-hidden z-[301]',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 503 },
          },
          React.createElement(
            'div',
            {
              className: 'bg-stone-900 text-white p-6 text-center',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 504 },
            },
            React.createElement(
              'div',
              {
                className:
                  'w-16 h-16 bg-gold-600/20 rounded-full flex items-center justify-center mx-auto mb-4',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 505 },
              },
              promptAction === 'cart'
                ? React.createElement(ShoppingBag, {
                    size: 32,
                    className: 'text-gold-400',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 507 },
                  })
                : React.createElement(Heart, {
                    size: 32,
                    className: 'text-gold-400',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 509 },
                  })
            ),
            React.createElement(
              'h3',
              {
                className: 'font-serif text-2xl mb-2',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 512 },
              },
              'Login Required'
            ),
            React.createElement(
              'p',
              {
                className: 'text-stone-400 text-sm',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 513 },
              },
              'Please login or create an account to add items to your ',
              promptAction,
              '.'
            )
          ),

          React.createElement(
            'div',
            {
              className: 'p-8 space-y-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 518 },
            },
            React.createElement(
              'button',
              {
                onClick: () => {
                  setShowLoginPrompt(false);
                  onNavigate('auth');
                },
                className:
                  'w-full bg-gold-600 text-white py-3 rounded-sm hover:bg-gold-700 transition-colors flex items-center justify-center gap-2 text-sm uppercase tracking-widest',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 519 },
              },

              React.createElement(LogIn, {
                size: 18,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 526 },
              }),
              'Login to Your Account'
            ),

            React.createElement(
              'button',
              {
                onClick: () => {
                  setShowLoginPrompt(false);
                  onNavigate('register');
                },
                className:
                  'w-full border border-stone-300 text-stone-700 py-3 rounded-sm hover:bg-stone-50 transition-colors flex items-center justify-center gap-2 text-sm uppercase tracking-widest',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 530 },
              },

              React.createElement(UserPlus, {
                size: 18,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 537 },
              }),
              'Create New Account'
            ),

            React.createElement(
              'button',
              {
                onClick: () => setShowLoginPrompt(false),
                className:
                  'w-full text-stone-500 py-2 text-xs hover:text-stone-700 transition-colors',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 541 },
              },
              'Continue Browsing'
            )
          )
        )
      ),

    React.createElement(
      'section',
      {
        className: 'relative h-[50vh] overflow-hidden flex items-center justify-center',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 552 },
      },
      React.createElement(
        'div',
        {
          className: 'absolute inset-0',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 553 },
        },
        React.createElement('img', {
          src: heroImages[activeCategory] || heroImages.all,
          alt: activeCategory,
          className: 'w-full h-full object-cover animate-[zoomSlow_20s_infinite_alternate]',
          onError: (e) => {
            const target = e.target;
            target.src = heroImages.all;
          },
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 554 },
        }),
        React.createElement('div', {
          className: 'absolute inset-0 bg-stone-900/40',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 563 },
        })
      ),

      React.createElement(
        'div',
        {
          className: 'relative z-10 text-center px-6 max-w-4xl text-white mt-10',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 566 },
        },
        React.createElement(
          'span',
          {
            className: 'text-gold-300 text-xs tracking-[0.3em] uppercase font-bold block mb-4',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 567 },
          },
          'Official Jewelry Shop'
        ),
        React.createElement(
          'h1',
          {
            className: 'font-serif text-5xl md:text-7xl mb-6 shadow-sm',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 570 },
          },
          heroTitles[activeCategory] || heroTitles.all
        ),
        React.createElement(
          'p',
          {
            className:
              'text-stone-100 text-lg font-light max-w-xl mx-auto backdrop-blur-sm bg-stone-900/10 p-4 rounded-sm',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 573 },
          },
          heroSubtitles[activeCategory] || heroSubtitles.all
        )
      )
    ),

    React.createElement(
      'div',
      {
        className:
          'sticky top-24 z-40 bg-white/90 backdrop-blur-md border-b border-stone-200 py-4 px-6 shadow-sm',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 579 },
      },
      React.createElement(
        'div',
        {
          className: 'max-w-[1440px] mx-auto',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 580 },
        },
        /* Category Row - Now separate */
        React.createElement(
          'div',
          {
            className:
              'flex items-center justify-center md:justify-start overflow-x-auto pb-3 md:pb-4 scrollbar-hide',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 582 },
          },
          React.createElement(
            'div',
            {
              className: 'flex items-center gap-2',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 583 },
            },
            categories.map((cat) =>
              React.createElement(
                'button',
                {
                  key: cat,
                  onClick: () => setActiveCategory(cat),
                  className: `px-6 py-2 rounded-full text-xs uppercase tracking-widest whitespace-nowrap transition-all duration-300 ${
                    activeCategory === cat
                      ? 'bg-stone-900 text-white shadow-lg'
                      : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
                  }`,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 585 },
                },

                cat === 'all' ? 'View All' : cat
              )
            )
          )
        ),

        /* Filter and Sort Row - Separate from categories */
        React.createElement(
          'div',
          {
            className:
              'flex flex-col md:flex-row justify-between items-center gap-4 pt-2 border-t border-stone-100',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 601 },
          },
          React.createElement(
            'div',
            {
              className: 'flex items-center gap-2 text-stone-500 text-xs tracking-widest',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 602 },
            },
            React.createElement(
              'span',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 603 } },
              visibleProducts.length,
              ' Items'
            )
          ),

          React.createElement(
            'div',
            {
              className: 'flex items-center gap-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 606 },
            },
            React.createElement(
              'button',
              {
                onClick: () => setShowFilters(!showFilters),
                className:
                  'flex items-center gap-2 text-stone-800 hover:text-gold-600 transition-colors text-xs uppercase font-bold',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 607 },
              },

              React.createElement(Filter, {
                size: 16,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 611 },
              }),
              ' Filters'
            ),

            React.createElement(
              'div',
              {
                className: 'relative group',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 614 },
              },
              React.createElement(
                'button',
                {
                  className:
                    'flex items-center gap-2 text-stone-800 hover:text-gold-600 transition-colors text-xs uppercase font-bold',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 615 },
                },
                'Sort ',
                React.createElement(ChevronDown, {
                  size: 14,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 616 },
                })
              ),
              React.createElement(
                'div',
                {
                  className:
                    'absolute right-0 top-full mt-2 w-48 bg-white border border-stone-100 shadow-xl rounded-sm py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 z-50',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 618 },
                },
                [
                  { label: 'Featured', val: 'featured' },
                  { label: 'Newest Arrivals', val: 'newest' },
                  { label: 'Price: Low to High', val: 'price-asc' },
                  { label: 'Price: High to Low', val: 'price-desc' },
                ].map((opt) =>
                  React.createElement(
                    'button',
                    {
                      key: opt.val,
                      onClick: () => setSortBy(opt.val),
                      className: `block w-full text-left px-4 py-3 text-xs uppercase tracking-wider hover:bg-stone-50 ${
                        sortBy === opt.val ? 'text-gold-600 font-bold' : 'text-stone-600'
                      }`,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 625 },
                    },

                    opt.label
                  )
                )
              )
            ),

            React.createElement(
              'button',
              {
                onClick: refreshProducts,
                className: 'text-stone-400 hover:text-gold-600 transition-colors',
                title: 'Refresh products',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 640 },
              },

              React.createElement(RefreshCw, {
                size: 16,
                className: apiLoading ? 'animate-spin' : '',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 645 },
              })
            )
          )
        )
      )
    ),

    apiError &&
      React.createElement(
        'div',
        {
          className: 'bg-red-50 border-l-4 border-red-500 p-4 mx-6 my-4',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 653 },
        },
        React.createElement(
          'div',
          {
            className: 'flex items-center',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 654 },
          },
          React.createElement(
            'div',
            {
              className: 'ml-3',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 655 },
            },
            React.createElement(
              'p',
              {
                className: 'text-sm text-red-700',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 656 },
              },
              apiError,
              ' - Showing available products'
            )
          ),
          React.createElement(
            'button',
            {
              onClick: refreshProducts,
              className: 'ml-auto text-sm text-red-600 hover:text-red-800 underline',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 660 },
            },
            'Retry'
          )
        )
      ),

    React.createElement(
      'section',
      {
        className: 'max-w-[1440px] mx-auto px-6 py-12 min-h-[600px]',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 670 },
      },
      loading || apiLoading
        ? React.createElement(
            'div',
            {
              className: 'w-full h-96 flex flex-col items-center justify-center text-gold-600',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 672 },
            },
            React.createElement('div', {
              className:
                'w-12 h-12 border-4 border-gold-200 border-t-gold-600 rounded-full animate-spin mb-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 673 },
            }),
            React.createElement(
              'span',
              {
                className: 'text-xs tracking-widest uppercase',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 674 },
              },
              'Loading treasures...'
            )
          )
        : visibleProducts.length === 0
          ? React.createElement(
              'div',
              {
                className: 'text-center py-20',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 679 },
              },
              React.createElement(
                'h3',
                {
                  className: 'font-serif text-2xl text-stone-400',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 680 },
                },
                'No products found.'
              ),
              React.createElement(
                'button',
                {
                  onClick: () => {
                    setActiveCategory('all');
                    setShowFilters(false);
                  },
                  className:
                    'mt-4 text-xs underline uppercase tracking-widest text-gold-600 hover:text-gold-500',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 681 },
                },
                'Reset Filters'
              )
            )
          : React.createElement(
              'div',
              {
                className: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-16',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 692 },
              },
              visibleProducts.map((product) => {
                const productImage = getProductImage(product);
                const isWishlistBusy =
                  !!wishlistLoading[product.id] || !!pendingWishlistAction[product.id];
                const isCartBusy = !!cartLoading[product.id];

                const extendedProduct = product;
                const isPromo =
                  extendedProduct.is_promo === true ||
                  extendedProduct.is_promo === 1 ||
                  extendedProduct.is_promo === '1' ||
                  (extendedProduct.promo_price && extendedProduct.promo_price > 0);

                const promoPrice = extendedProduct.promo_price;
                const promoPercentage = extendedProduct.promo_percentage;
                const promoLabel = extendedProduct.promo_label || 'SALE';
                const promoEndDate = extendedProduct.promo_end_date;
                const formattedEndDate = formatDate(promoEndDate);

                const isPromoActive = !promoEndDate || new Date(promoEndDate) >= new Date();
                const showPromo = isPromo && isPromoActive && promoPrice;

                const displayPrice = showPromo ? promoPrice : product.price || 0;
                const originalPrice = showPromo ? product.price : null;

                const isPreLoved =
                  product.condition === 'Pre-loved' || product.condition === 'Pre-loved (Used)';
                const isPreOrder = extendedProduct.isPreOrder || false;

                const outOfStock = typeof product.stock === 'number' && product.stock <= 0;

                let displayWeight = '';
                if (product.weight) {
                  if (typeof product.weight === 'number') {
                    displayWeight = product.weight.toFixed(1);
                  } else {
                    const parsed = parseFloat(product.weight);
                    displayWeight = !isNaN(parsed) ? parsed.toFixed(1) : String(product.weight);
                  }
                }

                return React.createElement(
                  'div',
                  {
                    key: product.id,
                    className: 'group relative cursor-pointer',
                    onMouseEnter: () => setHoveredProduct(product.id),
                    onMouseLeave: () => setHoveredProduct(null),
                    onClick: () => handleProductClick(product),
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 739 },
                  },

                  React.createElement(
                    'div',
                    {
                      className: 'relative aspect-[3/4] overflow-hidden bg-stone-50 mb-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 746 },
                    },
                    React.createElement(
                      'div',
                      {
                        className: 'absolute top-3 left-3 z-20 flex flex-col gap-2',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 747 },
                      },
                      showPromo &&
                        React.createElement(
                          'div',
                          {
                            className: 'flex flex-col gap-1',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 749 },
                          },
                          React.createElement(
                            'span',
                            {
                              className:
                                'bg-red-600/90 backdrop-blur text-white text-[10px] uppercase font-bold px-2 py-1 tracking-wider shadow-sm flex items-center gap-1',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 750 },
                            },
                            React.createElement(Tag, {
                              size: 12,
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 751 },
                            }),
                            ' ',
                            promoLabel
                          ),
                          promoPercentage &&
                            React.createElement(
                              'span',
                              {
                                className:
                                  'bg-red-500/90 backdrop-blur text-white text-[8px] uppercase font-bold px-2 py-0.5 tracking-wider shadow-sm',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 754 },
                              },
                              '-',
                              promoPercentage,
                              '% OFF'
                            )
                        ),

                      product.isNew &&
                        !showPromo &&
                        React.createElement(
                          'span',
                          {
                            className:
                              'bg-white/90 backdrop-blur text-stone-900 text-[10px] uppercase font-bold px-2 py-1 tracking-wider shadow-sm',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 762 },
                          },
                          'New Arrival'
                        ),

                      product.isBestSeller &&
                        !showPromo &&
                        React.createElement(
                          'span',
                          {
                            className:
                              'bg-gold-600/90 backdrop-blur text-white text-[10px] uppercase font-bold px-2 py-1 tracking-wider shadow-sm',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 768 },
                          },
                          'Best Seller'
                        ),

                      isPreOrder &&
                        !showPromo &&
                        React.createElement(
                          'span',
                          {
                            className:
                              'bg-purple-600/90 backdrop-blur text-white text-[10px] uppercase font-bold px-2 py-1 tracking-wider shadow-sm flex items-center gap-1',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 774 },
                          },
                          React.createElement(Clock, {
                            size: 12,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 775 },
                          }),
                          ' Pre-Order'
                        ),

                      outOfStock &&
                        React.createElement(
                          'span',
                          {
                            className:
                              'bg-red-700/90 backdrop-blur text-white text-[10px] uppercase font-bold px-2 py-1 tracking-wider shadow-sm',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 780 },
                          },
                          'Out of Stock'
                        )
                    ),

                    product.condition &&
                      product.condition !== 'Brand New' &&
                      !isPreOrder &&
                      !showPromo &&
                      React.createElement(
                        'div',
                        {
                          className: 'absolute top-3 right-3 z-20',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 790 },
                        },
                        React.createElement(
                          'span',
                          {
                            className: `backdrop-blur text-[10px] uppercase font-bold px-2 py-1 tracking-wider shadow-sm flex items-center gap-1 ${getConditionBadgeStyle(
                              product.condition
                            )}`,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 791 },
                          },

                          getConditionDisplay(product.condition)
                        )
                      ),

                    product.condition === 'Brand New' &&
                      !product.isNew &&
                      !product.isBestSeller &&
                      !isPreOrder &&
                      !showPromo &&
                      React.createElement(
                        'div',
                        {
                          className: 'absolute top-3 left-3 z-20',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 806 },
                        },
                        React.createElement(
                          'span',
                          {
                            className: `backdrop-blur text-[10px] uppercase font-bold px-2 py-1 tracking-wider shadow-sm ${getConditionBadgeStyle(
                              product.condition
                            )}`,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 807 },
                          },

                          product.condition
                        )
                      ),

                    React.createElement(
                      'button',
                      {
                        onClick: (e) => handleWishlistToggle(e, product),
                        disabled: isWishlistBusy || (outOfStock && !isInWishlist(product.id)),
                        title: outOfStock && !isInWishlist(product.id) ? 'Out of stock' : '',
                        className: `absolute top-3 right-3 z-20 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                          isInWishlist(product.id)
                            ? 'bg-red-50 text-red-500'
                            : outOfStock
                              ? 'bg-white/30 text-white/40 cursor-not-allowed'
                              : 'bg-white/50 text-white hover:bg-white hover:text-red-500'
                        } ${isWishlistBusy ? 'opacity-50 cursor-not-allowed' : ''}`,
                        style: {
                          right:
                            product.condition &&
                            product.condition !== 'Brand New' &&
                            !isPreOrder &&
                            !showPromo
                              ? '3.5rem'
                              : '0.75rem',
                        },
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 817 },
                      },

                      isWishlistBusy
                        ? React.createElement('div', {
                            className:
                              'w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 836 },
                          })
                        : React.createElement(Heart, {
                            size: 18,
                            fill: isInWishlist(product.id) ? 'currentColor' : 'none',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 838 },
                          })
                    ),

                    product.galleryImages &&
                      product.galleryImages.length > 0 &&
                      React.createElement(
                        'div',
                        {
                          className: 'absolute bottom-3 left-3 z-20 flex gap-1',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 846 },
                        },
                        React.createElement(
                          'div',
                          {
                            className: 'flex -space-x-1',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 847 },
                          },
                          product.galleryImages.slice(0, 3).map((_, idx) =>
                            React.createElement('div', {
                              key: idx,
                              className: 'w-1.5 h-1.5 rounded-full bg-white/80 shadow-sm',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 849 },
                            })
                          ),
                          product.galleryImages.length > 3 &&
                            React.createElement(
                              'span',
                              {
                                className: 'text-[8px] text-white ml-1',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 855 },
                              },
                              '+',
                              product.galleryImages.length - 3
                            )
                        )
                      ),

                    React.createElement('img', {
                      src: productImage,
                      alt: product.name,
                      className:
                        'absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105',
                      onError: () => handleImageError(product.id),
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 863 },
                    }),

                    React.createElement(
                      'div',
                      {
                        className: `absolute bottom-0 left-0 w-full p-4 bg-gradient-to-t from-stone-900 via-stone-900/80 to-transparent transition-transform duration-300 transform ${
                          hoveredProduct === product.id ? 'translate-y-0' : 'translate-y-full'
                        }`,
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 870 },
                      },

                      product.galleryImages &&
                        product.galleryImages.length > 0 &&
                        React.createElement(
                          'button',
                          {
                            className:
                              'w-10 bg-stone-800 hover:bg-stone-700 text-white py-3 flex items-center justify-center transition-colors rounded-sm',
                            title: 'View Gallery',
                            onClick: (e) => openLightbox(product.galleryImages, 0, e),
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 879 },
                          },

                          React.createElement(Eye, {
                            size: 16,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 884 },
                          })
                        )
                    )
                  ),

                  React.createElement(
                    'div',
                    {
                      className:
                        'text-center mt-3 group-hover:-translate-y-1 transition-transform duration-300',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 890 },
                    },
                    React.createElement(
                      'h3',
                      {
                        className:
                          'font-serif text-lg text-stone-900 mb-1 group-hover:text-gold-600 transition-colors',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 891 },
                      },
                      product.name
                    ),

                    showPromo &&
                      React.createElement(
                        'div',
                        {
                          className: 'flex justify-center items-center gap-1 mb-2',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 896 },
                        },
                        React.createElement(
                          'span',
                          {
                            className:
                              'text-[9px] bg-red-600 text-white px-2 py-0.5 rounded-full uppercase font-bold flex items-center gap-1',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 897 },
                          },
                          React.createElement(Tag, {
                            size: 10,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 898 },
                          }),
                          ' ',
                          promoLabel
                        ),
                        promoPercentage &&
                          React.createElement(
                            'span',
                            {
                              className: 'text-[9px] text-red-600 font-bold',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 901 },
                            },
                            '-',
                            promoPercentage,
                            '%'
                          )
                      ),

                    isPreOrder &&
                      !showPromo &&
                      React.createElement(
                        'div',
                        {
                          className: 'flex justify-center mb-2',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 909 },
                        },
                        React.createElement(
                          'span',
                          {
                            className:
                              'text-[9px] text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full inline-flex items-center gap-1',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 910 },
                          },
                          React.createElement(Clock, {
                            size: 10,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 911 },
                          }),
                          ' Pre-order now'
                        )
                      ),

                    isPreLoved &&
                      !showPromo &&
                      React.createElement(
                        'div',
                        {
                          className: 'flex justify-center mb-2',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 917 },
                        },
                        React.createElement(
                          'span',
                          {
                            className:
                              'text-[9px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full inline-flex items-center gap-1',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 918 },
                          },
                          React.createElement(
                            'span',
                            { __self: this, __source: { fileName: _jsxFileName, lineNumber: 919 } },
                            '🔄'
                          ),
                          ' Pre-owned - Authentic'
                        )
                      ),

                    activeCategory === 'jewelry' &&
                      product.category === 'jewelry' &&
                      (product.weight || product.karat || product.goldType) &&
                      React.createElement(
                        'div',
                        {
                          className: 'flex flex-wrap justify-center gap-1 mb-2 px-4',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 927 },
                        },
                        product.karat &&
                          React.createElement(
                            'span',
                            {
                              className:
                                'text-[9px] uppercase tracking-wider border border-stone-200 px-1 rounded-sm text-stone-500',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 929 },
                            },
                            product.karat
                          ),
                        product.goldType &&
                          React.createElement(
                            'span',
                            {
                              className:
                                'text-[9px] uppercase tracking-wider border border-stone-200 px-1 rounded-sm text-stone-500',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 934 },
                            },
                            product.goldType.replace('_', ' ')
                          ),
                        displayWeight &&
                          React.createElement(
                            'span',
                            {
                              className:
                                'text-[9px] uppercase tracking-wider border border-stone-200 px-1 rounded-sm text-stone-500',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 939 },
                            },
                            displayWeight,
                            'g'
                          )
                      ),

                    React.createElement(
                      'p',
                      {
                        className: 'text-xs text-stone-500 font-light mb-2 line-clamp-1 px-4',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 946 },
                      },
                      product.description
                    ),

                    React.createElement(
                      'div',
                      {
                        className: 'flex flex-col items-center',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 950 },
                      },
                      showPromo &&
                        originalPrice &&
                        React.createElement(
                          'div',
                          {
                            className: 'flex items-center gap-2 mb-1',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 952 },
                          },
                          React.createElement(
                            'span',
                            {
                              className: 'text-xs text-stone-400 line-through',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 953 },
                            },
                            formatPrice(originalPrice)
                          ),
                          promoPercentage &&
                            React.createElement(
                              'span',
                              {
                                className:
                                  'text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-bold',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 957 },
                              },
                              '-',
                              promoPercentage,
                              '%'
                            )
                        ),

                      React.createElement(
                        'span',
                        {
                          className: `text-sm font-bold ${
                            showPromo ? 'text-red-600' : 'text-stone-900'
                          } tracking-wide`,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 964 },
                        },

                        formatPrice(displayPrice)
                      ),

                      showPromo &&
                        formattedEndDate &&
                        React.createElement(
                          'span',
                          {
                            className: 'text-[8px] text-stone-400 mt-1',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 973 },
                          },
                          'Ends ',
                          formattedEndDate
                        )
                    )
                  )
                );
              })
            )
    ),

    lightboxOpen &&
      React.createElement(
        'div',
        {
          className: 'fixed inset-0 z-[300] bg-black/95 flex items-center justify-center',
          onClick: () => setLightboxOpen(false),
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 987 },
        },

        React.createElement(
          'button',
          {
            onClick: () => setLightboxOpen(false),
            className:
              'absolute top-5 right-5 text-white hover:text-gold-600 z-10 bg-black/50 p-2 rounded-full transition-colors',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 991 },
          },

          React.createElement(X, {
            size: 30,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 995 },
          })
        ),

        lightboxImages.length > 1 &&
          React.createElement(
            React.Fragment,
            null,
            React.createElement(
              'button',
              {
                onClick: (e) => {
                  e.stopPropagation();
                  setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : lightboxImages.length - 1));
                },
                className:
                  'absolute left-5 text-white hover:text-gold-600 bg-black/50 p-2 rounded-full transition-colors',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1000 },
              },

              React.createElement(ChevronLeft, {
                size: 40,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1009 },
              })
            ),
            React.createElement(
              'button',
              {
                onClick: (e) => {
                  e.stopPropagation();
                  setCurrentImageIndex((prev) => (prev < lightboxImages.length - 1 ? prev + 1 : 0));
                },
                className:
                  'absolute right-5 text-white hover:text-gold-600 bg-black/50 p-2 rounded-full transition-colors',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1011 },
              },

              React.createElement(ChevronRight, {
                size: 40,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1020 },
              })
            )
          ),

        React.createElement('img', {
          src: lightboxImages[currentImageIndex],
          alt: 'Product view',
          className: 'max-w-[90vw] max-h-[90vh] object-contain',
          onClick: (e) => e.stopPropagation(),
          onError: (e) => {
            const target = e.target;
            target.src = PLACEHOLDER_IMAGE;
          },
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 1025 },
        }),

        lightboxImages.length > 1 &&
          React.createElement(
            'div',
            {
              className:
                'absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2 bg-black/70 p-2 rounded-lg overflow-x-auto max-w-[90vw]',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1037 },
            },
            lightboxImages.map((img, idx) =>
              React.createElement(
                'button',
                {
                  key: idx,
                  onClick: (e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(idx);
                  },
                  className: `flex-shrink-0 w-12 h-12 rounded-sm overflow-hidden border-2 transition-colors ${
                    idx === currentImageIndex
                      ? 'border-gold-600'
                      : 'border-transparent hover:border-white/50'
                  }`,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1039 },
                },

                React.createElement('img', {
                  src: img,
                  alt: `Thumb ${idx + 1}`,
                  className: 'w-full h-full object-cover',
                  onError: (e) => {
                    const target = e.target;
                    target.src = PLACEHOLDER_IMAGE;
                  },
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1051 },
                })
              )
            )
          )
      )
  );
};

export default Shop;
